import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Box, Button, Stack, TextField, Typography } from '@mui/material';
import { FileUploadOutlined } from '@mui/icons-material';
import deleteIcon from '../../../../../assets/icons/delete.svg';
import { setToastMessage } from '@stores/App/slice';
import downloadIcon from '@assets/icons/download_icon_green.svg';
import { TOAST_TYPES } from '@constants/constants';
import { useSelfServeAdminAggregatedCohortDataFileUploadMutation } from '@api/selfServe';

interface AggregatedCohortDataFormProps {
  connectionDetails: string;
  refreshRequired: boolean;
  handleUpdateNewGameData: any;
  onValidityChange?: (isValid: boolean) => void;
  disabled?: boolean;
}

// const AGGREGATED_COHORT_DATA_PUBLIC_URL_REGEX =
//   /^https:\/\/docs\.google\.com\/spreadsheets\/(?:u\/\d+\/)?d\/[a-zA-Z0-9-_]+(?:\/[^?#]*)?(?:\?[^#]*)?(?:#.*)?$/;

const AggregatedCohortDataForm: React.FC<AggregatedCohortDataFormProps> = ({
  connectionDetails,
  refreshRequired,
  handleUpdateNewGameData,
  onValidityChange,
  disabled = false,
}) => {
  const fieldValues = JSON.parse(connectionDetails || '{}');
  const dispatch = useDispatch();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [attachment, setAttachment] = useState<{
    fileName: string;
    filePath: string;
    fileType: string;
  } | null>(null);
  const [isTouched, setIsTouched] = useState({
    publicUrlField: false,
  });

  useEffect(() => {
    const isPublicUrlValid = !!fieldValues?.publicUrl || !!attachment;

    onValidityChange?.(isPublicUrlValid);
  }, [fieldValues.publicUrl, attachment]);

  const [uploadAggregatedCohortDataFile] =
    useSelfServeAdminAggregatedCohortDataFileUploadMutation();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    const validTypes = [
      'application/vnd.ms-excel', // .xls
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
      'text/csv',
    ];
    const isValidType = validTypes.includes(file?.type || '');
    if (!isValidType) {
      dispatch(
        setToastMessage({
          type: TOAST_TYPES.ERROR,
          message: `File "${file?.name}" is not a supported format.`,
        }),
      );
      return;
    }
    if (file && isValidType) {
      uploadAggregatedCohortDataFile({ file })
        .unwrap()
        .then((res) => {
          if (res?.data) {
            setAttachment({
              fileName: file.name,
              filePath: res.data.filePath,
              fileType: res.data.fileType,
            });

            handleUpdateNewGameData(
              'connectionDetails',
              JSON.stringify({
                ...fieldValues,
                type: 'FILE_UPLOAD',
                filePath: res.data.filePath,
                fileType: res.data.fileType,
                publicUrl: undefined,
              }),
            );
          }
        });
    }
  };

  const handleDownloadAttachment = async () => {
    if (!fieldValues?.signedUrl) return;

    const link = document.createElement('a');
    link.href = fieldValues?.signedUrl;
    link.target = '_blank';
    link.download =
      fieldValues?.filePath || `Aggregated Cohort Data.${fieldValues?.fileType || 'csv'}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (fieldValues?.signedUrl) {
    return (
      <Stack
        gap={'0.75rem'}
        sx={{
          backgroundColor: '#fff',
          borderRadius: '0.75rem',
          padding: '0.5rem 0rem',
          width: '90%',
          marginX: '0.5rem',
        }}
      >
        <Typography
          sx={{
            fontFamily: 'Manrope, sans-serif',
            fontWeight: 700,
            fontSize: '0.85rem',
            color: '#162936',
            marginRight: '0.2rem',
          }}
        >
          {fieldValues?.filePath}
        </Typography>
        <Box
          sx={{
            width: 'fit-content',
            display: 'flex',
            gap: '0.5rem',
            textAlign: 'left',
            border: '1px solid #E0E0E0',
            borderRadius: '0.5rem',
            padding: '0.5rem 1rem',
            cursor: 'pointer',
          }}
          onClick={(e) => {
            handleDownloadAttachment();
            e.stopPropagation();
          }}
        >
          <Typography
            sx={{
              fontFamily: 'Manrope, sans-serif',
              fontWeight: 700,
              fontSize: '0.85rem',
              color: '#162936',
              marginRight: '0.2rem',
            }}
          >
            Download
          </Typography>
          <img src={downloadIcon} alt="upload-icon" style={{ userSelect: 'none' }} />
        </Box>
      </Stack>
    );
  }

  return (
    <Stack
      gap={'0.75rem'}
      sx={{
        backgroundColor: '#fff',
        borderRadius: '0.75rem',
        padding: '0.5rem 0rem',
        width: '100%',
        marginX: '0.5rem',
      }}
    >
      {!fieldValues.publicUrl ? (
        <>
          <Typography
            sx={{
              fontWeight: 500,
              fontSize: '0.875rem',
              lineHeight: '1.25rem',
              color: '#162936',
            }}
          >
            Upload your Cohort File ( .xls, .xlsx, .csv only)
          </Typography>
          {attachment === null ? (
            <Button
              variant="contained"
              color="primary"
              onClick={() => fileInputRef.current?.click()}
              sx={{
                padding: '0.75rem 1.5rem',
                textTransform: 'none',
                width: 'fit-content',
                border: '1px solid #2E4A4F',
                borderRadius: '100px',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                backgroundColor: 'white',
                fontWeight: 700,
                fontSize: '0.875rem',
                lineHeight: '1.25rem',
                letterSpacing: '-0.35px',
                color: '#6C6C6C',
                boxShadow: 'none',
                '&:hover': {
                  backgroundColor: 'white',
                  boxShadow: 'none',
                },
              }}
              disabled={disabled}
            >
              <FileUploadOutlined
                sx={{
                  height: '1.125rem',
                  width: '1.125rem',
                  color: '#2E4A4F',
                }}
              />
              Upload File
              <input
                type="file"
                accept=".xls, .xlsx, .csv"
                multiple
                hidden
                ref={fileInputRef}
                onChange={handleFileChange}
              />
            </Button>
          ) : (
            <Box
              sx={{
                width: '25rem',
                backgroundColor: '#F0F0F1',
                padding: '0.5rem 1rem',
                borderRadius: '0.5rem',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-start',
                  alignItems: 'center',
                  flex: 1, // Allow this box to grow and shrink
                  minWidth: 0, // Important for text ellipsis to work
                }}
              >
                <Typography
                  sx={{
                    fontFamily: 'Manrope, sans-serif',
                    fontWeight: 700,
                    fontSize: '1rem',
                    color: '#162936',
                    marginRight: '0.2rem',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: '100%', // Ensure it doesn't exceed container
                  }}
                >
                  {attachment?.fileName || ''}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                  flexShrink: 0, // Prevent the delete button from shrinking
                }}
                onClick={() => {
                  setAttachment(null);
                  handleUpdateNewGameData(
                    'connectionDetails',
                    JSON.stringify({
                      ...fieldValues,
                      type: undefined,
                      filePath: undefined,
                      fileType: undefined,
                      publicUrl: undefined,
                    }),
                  );
                }}
              >
                <img height={16} width={16} src={deleteIcon} alt="rating-star" />
              </Box>
            </Box>
          )}
        </>
      ) : (
        <></>
      )}
      {attachment === null && !fieldValues.publicUrl ? (
        <Typography
          sx={{
            fontWeight: 500,
            fontSize: '0.875rem',
            lineHeight: '1.25rem',
            color: '#162936',
          }}
        >
          Or
        </Typography>
      ) : (
        <></>
      )}
      {attachment === null ? (
        <>
          <TextField
            label="Add Google Sheet Link"
            placeholder="Add Public Link here"
            sx={{
              width: '100%',
            }}
            value={fieldValues.publicUrl}
            onChange={(e) =>
              handleUpdateNewGameData(
                'connectionDetails',
                JSON.stringify({
                  ...fieldValues,
                  type: 'PUBLIC_URL',
                  publicUrl: e.target.value,
                  filePath: undefined,
                  fileType: undefined,
                }),
              )
            }
            error={!fieldValues.publicUrl && isTouched.publicUrlField}
            onBlur={(e) => {
              setIsTouched((prev) => ({
                ...prev,
                publicUrlField: true,
              }));
            }}
            disabled={disabled}
          />
          {!disabled && (
            <Typography
              sx={{
                fontWeight: 400,
                fontSize: '0.75rem',
                lineHeight: '1.25rem',
                color: '#6C6C6C',
              }}
            >
              Share Editor access with underwriting@pvxpartners.com
            </Typography>
          )}
        </>
      ) : (
        <></>
      )}
    </Stack>
  );
};

export default AggregatedCohortDataForm;
